import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { format, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'
import {
  ArrowLeft,
  Pencil,
  Calendar,
  Share2,
  Loader2,
  FileText,
  Eye,
  Link2,
  Archive,
  MoreHorizontal
} from 'lucide-react'
import FeaturedLayout from '../layouts/FeaturedLayout'
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../ui'
import { useConfirmDialog } from '../../hooks/useConfirmDialog'
import { usePermissions } from '../../hooks/usePermissions'
import { useAuth } from '../../hooks/useAuth'
import {
  getProgramaWithSeccionesYHabilidades,
  generateTrainingFileUrl,
  archiveTrainingProgram
} from '../../lib/supabase'
import { supabase } from '../../lib/supabaseClient'
import { getSeccionTipoLabel, getSeccionTipoColor } from '../../constants/seccionTipos'
import { formatDuracionTotal } from './timetableHelpers'
import ProgramaEditorModal from './ProgramaEditorModal'
import AsignacionModal from './AsignacionModal'
import ProgramaTiposBar from './ProgramaTiposBar'

const formatMmSs = (totalSeg) => {
  const m = Math.floor((totalSeg || 0) / 60)
  const s = (totalSeg || 0) % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

const ProgramaDetalleView = () => {
  const { programaId } = useParams()
  const navigate = useNavigate()
  const { staff } = useAuth()
  const { canManagePrograms } = usePermissions()
  const { confirm, alert: alertDialog, ConfirmDialog } = useConfirmDialog()

  const [programa, setPrograma] = useState(null)
  const [archivos, setArchivos] = useState([])
  const [asignaciones, setAsignaciones] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editorOpen, setEditorOpen] = useState(false)
  const [asignacionOpen, setAsignacionOpen] = useState(false)
  const [archivando, setArchivando] = useState(false)

  const load = async () => {
    if (!programaId) return
    setLoading(true)
    setError(null)
    const { data, error: err } = await getProgramaWithSeccionesYHabilidades(programaId)
    if (err) {
      setError('No pudimos cargar el programa')
      setLoading(false)
      return
    }
    setPrograma(data)

    // Archivos (vienen en el programa via el join)
    const { data: archivosRows } = await supabase
      .from('programas_entrenamiento_archivos')
      .select('*')
      .eq('programa_id', programaId)
      .order('created_at', { ascending: true })
    setArchivos(archivosRows || [])

    // Asignaciones recientes (proximos 60 dias)
    const { data: asignacionesRows } = await supabase
      .from('programas_entrenamiento_agenda')
      .select('id, fecha, grupo:grupos(id, nombre_del_grupo), reloj_compartido_entre_grupos, grupo_sync_token')
      .eq('programa_id', programaId)
      .eq('activo', true)
      .order('fecha', { ascending: true })
      .limit(30)
    setAsignaciones(asignacionesRows || [])

    setLoading(false)
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programaId])

  const handleDescargarArchivo = async (archivo) => {
    try {
      const { data, error: err } = await generateTrainingFileUrl(archivo.storage_path)
      if (err || !data?.signedUrl) throw err || new Error('Sin URL')
      window.open(data.signedUrl, '_blank')
    } catch (err) {
      await alertDialog('Error', 'No pudimos abrir el archivo')
    }
  }

  const handleArchivar = async () => {
    const ok = await confirm(
      'Archivar programa',
      `¿Archivar "${programa.titulo}"? Dejara de aparecer en la biblioteca y no se podra asignar a nuevas sesiones. Los programas ya asignados a sesiones pasadas no se ven afectados.`,
      { destructive: true, confirmText: 'Archivar' }
    )
    if (!ok) return
    setArchivando(true)
    const { error: err } = await archiveTrainingProgram(programaId)
    setArchivando(false)
    if (err) {
      await alertDialog('Error', err.message || 'No pudimos archivar')
      return
    }
    navigate('/programas/agenda')
  }

  const canEditPrograma = () => {
    if (!programa) return false
    if (canManagePrograms) return true
    if (!staff?.id) return false
    return programa.created_by && programa.created_by === staff.id
  }

  if (loading) {
    return (
      <FeaturedLayout title="Cargando...">
        <div className="flex items-center justify-center py-20 text-sm text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Cargando programa...
        </div>
      </FeaturedLayout>
    )
  }

  if (error || !programa) {
    return (
      <FeaturedLayout title="Error">
        <Alert variant="destructive" className="max-w-md">
          <AlertDescription>{error || 'Programa no encontrado'}</AlertDescription>
        </Alert>
        <Button variant="outline" onClick={() => navigate(-1)} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Volver
        </Button>
      </FeaturedLayout>
    )
  }

  const secciones = programa.secciones || []
  const habilidades = programa.habilidades || []
  const tags = programa.tags || []
  const deportes = programa.deportes || []
  const hoy = new Date().toISOString().split('T')[0]
  const asignacionesFuturas = asignaciones.filter((a) => a.fecha >= hoy)
  const asignacionesPasadas = asignaciones.filter((a) => a.fecha < hoy)

  return (
    <FeaturedLayout title={programa.titulo}>
      {/* Toolbar de acciones - jerarquia clara: 1 primaria + secundarias + menu */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Volver
        </Button>
        <div className="flex items-center gap-2">
          {/* Accion primaria: abre la vista compartible (donde se elige imagen o PDF) */}
          <Button
            size="sm"
            onClick={() => navigate(`/programas/${programaId}/imprimible`)}
          >
            <Share2 className="h-4 w-4 mr-1" />
            Compartir
          </Button>
          {/* Acciones de gestion */}
          {canEditPrograma() && (
            <Button variant="outline" size="sm" onClick={() => setEditorOpen(true)}>
              <Pencil className="h-4 w-4 mr-1" />
              Editar
            </Button>
          )}
          {canManagePrograms && (
            <Button variant="outline" size="sm" onClick={() => setAsignacionOpen(true)}>
              <Calendar className="h-4 w-4 mr-1" />
              Asignar
            </Button>
          )}
          {/* Menu para accion destructiva (archivar) */}
          {canEditPrograma() && programa.activo !== false && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" aria-label="Mas acciones">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={handleArchivar}
                  disabled={archivando}
                  className="text-red-600 focus:text-red-700"
                >
                  {archivando ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Archive className="h-4 w-4 mr-2" />
                  )}
                  Archivar programa
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Header con info principal */}
      <Card className="mb-4">
        <CardHeader className="pb-3">
          <CardTitle className="text-2xl">{programa.titulo}</CardTitle>
          {secciones.length > 0 && (
            <p className="text-sm text-muted-foreground mt-1">
              ⏱ {formatDuracionTotal(secciones)} · {secciones.length} secciones cronometradas
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Barra de tipos de seccion */}
          {secciones.length > 0 && (
            <ProgramaTiposBar secciones={secciones} />
          )}
          {programa.descripcion && (
            <p className="text-sm text-foreground whitespace-pre-line">{programa.descripcion}</p>
          )}

          {/* Tags */}
          {(deportes.length > 0 || habilidades.length > 0 || tags.length > 0) && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {deportes.map((d) => (
                <Badge key={`deporte-${d}`} variant="outline">{d}</Badge>
              ))}
              {habilidades.map((h) => (
                <Badge key={`hab-${h.id}`} variant="outline" className="border-blue-200 text-blue-700">
                  {h.nombre}
                </Badge>
              ))}
              {tags.map((t) => (
                <Badge key={`tag-${t}`} variant="secondary">{t}</Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Secciones */}
      <Card className="mb-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Secciones</CardTitle>
        </CardHeader>
        <CardContent>
          {secciones.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              Este programa no tiene secciones cronometradas. Solo material de apoyo.
            </p>
          ) : (
            <div className="space-y-2">
              {secciones.map((s, idx) => (
                <div
                  key={s.id || idx}
                  className="flex items-start gap-3 rounded-md border border-border px-3 py-2.5"
                >
                  <span className="text-muted-foreground font-mono text-sm w-6 pt-0.5">
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-foreground">{s.nombre}</p>
                      <Badge variant="outline" className={`text-[10px] ${getSeccionTipoColor(s.tipo)}`}>
                        {getSeccionTipoLabel(s.tipo)}
                      </Badge>
                    </div>
                    {s.descripcion && (
                      <p className="text-sm text-muted-foreground mt-1">{s.descripcion}</p>
                    )}
                  </div>
                  <span className="text-sm font-mono text-foreground shrink-0 pt-0.5">
                    {formatMmSs(s.duracion_seg)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Material adjunto */}
      {archivos.length > 0 && (
        <Card className="mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Material adjunto</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {archivos.map((a) => (
              <div
                key={a.id}
                className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-sm text-foreground truncate">{a.file_name}</span>
                  {a.file_size && (
                    <span className="text-xs text-muted-foreground shrink-0">
                      {Math.round(a.file_size / 1024)} KB
                    </span>
                  )}
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => handleDescargarArchivo(a)}>
                    <Eye className="h-3.5 w-3.5 mr-1" />
                    Ver
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Asignaciones */}
      {asignacionesFuturas.length > 0 && (
        <Card className="mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Proximas sesiones asignadas ({asignacionesFuturas.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5">
            {asignacionesFuturas.slice(0, 10).map((a) => (
              <div
                key={a.id}
                className="flex items-center gap-3 text-sm py-1 px-2 rounded hover:bg-muted/50"
              >
                <span className="font-mono text-muted-foreground w-32">
                  {format(parseISO(a.fecha), "EEE d 'de' MMM", { locale: es })}
                </span>
                <span className="text-foreground flex-1 truncate">
                  {a.grupo?.nombre_del_grupo || 'Sin grupo'}
                </span>
                {a.grupo_sync_token && (
                  <Badge variant="outline" className="text-[10px] border-blue-200 text-blue-700">
                    <Link2 className="h-3 w-3 mr-1" />
                    Reloj compartido
                  </Badge>
                )}
              </div>
            ))}
            {asignacionesFuturas.length > 10 && (
              <p className="text-xs text-muted-foreground italic pt-1">
                Y {asignacionesFuturas.length - 10} mas...
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {asignacionesPasadas.length > 0 && (
        <Card className="mb-4 opacity-80">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2 text-muted-foreground">
              <Calendar className="h-4 w-4" />
              Historial ({asignacionesPasadas.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5">
            {asignacionesPasadas.slice(0, 5).map((a) => (
              <div key={a.id} className="flex items-center gap-3 text-xs py-1 px-2 text-muted-foreground">
                <span className="font-mono w-32">
                  {format(parseISO(a.fecha), "EEE d 'de' MMM", { locale: es })}
                </span>
                <span className="flex-1 truncate">{a.grupo?.nombre_del_grupo || 'Sin grupo'}</span>
              </div>
            ))}
            {asignacionesPasadas.length > 5 && (
              <p className="text-xs text-muted-foreground italic pt-1">
                Y {asignacionesPasadas.length - 5} ejecuciones anteriores.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Modales */}
      <ProgramaEditorModal
        open={editorOpen}
        onOpenChange={(open) => {
          setEditorOpen(open)
          if (!open) load()
        }}
        programaId={programaId}
        onSaved={() => {
          setEditorOpen(false)
          load()
        }}
      />
      <AsignacionModal
        open={asignacionOpen}
        onOpenChange={(open) => {
          setAsignacionOpen(open)
          if (!open) load()
        }}
        programa={{ ...programa, id: programaId }}
        onAssigned={() => {
          setAsignacionOpen(false)
          load()
        }}
      />
      <ConfirmDialog />
    </FeaturedLayout>
  )
}

export default ProgramaDetalleView
